import { useState } from "react";
import DOMPurify from "dompurify";
import type { JobInternalFileLinkDto } from "@/lib/job-files";

export interface InternalLinksPanelProps {
  jobId: number;
  links: JobInternalFileLinkDto[];
  onSave: (req: { linkDisplayText: string; comment?: string; rowVersionBase64?: string }) => void;
  onDelete: (id: number, rowVersionBase64: string) => void;
}

const ALLOWED_TAGS: string[] = ["a", "b", "i", "br", "p"];
const ALLOWED_ATTR: string[] = ["href"];

function sanitize(html: string): string {
  return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR });
}

interface LinkFormState {
  linkDisplayText: string;
  comment: string;
}

export function InternalLinksPanel({ links, onSave, onDelete }: InternalLinksPanelProps) {
  const [form, setForm] = useState<LinkFormState>({ linkDisplayText: "", comment: "" });
  const [editId, setEditId] = useState<number | null>(null);
  const [editRv, setEditRv] = useState<string>("");

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSave({
      linkDisplayText: form.linkDisplayText,
      comment: form.comment || undefined,
      rowVersionBase64: editId != null ? editRv : undefined,
    });
    setForm({ linkDisplayText: "", comment: "" });
    setEditId(null);
    setEditRv("");
  }

  function startEdit(link: JobInternalFileLinkDto) {
    setEditId(link.jobInternalFileLinkId);
    setEditRv(link.rowVersionBase64);
    setForm({ linkDisplayText: link.linkDisplayText, comment: link.comment ?? "" });
  }

  return (
    <div className="space-y-4" data-testid="internal-links-panel">
      <h3 className="text-sm font-medium">Internal Links</h3>

      {links.length === 0 ? (
        <p className="text-sm text-muted-foreground">No internal links</p>
      ) : (
        <ul className="space-y-2">
          {links.map((link) => (
            <li key={link.jobInternalFileLinkId} className="rounded border border-border p-3 space-y-1">
              <div
                className="text-sm"
                dangerouslySetInnerHTML={{ __html: sanitize(link.linkDisplayText) }}
              />
              {link.comment && (
                <p className="text-xs text-muted-foreground">{link.comment}</p>
              )}
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  className="text-xs text-primary hover:underline"
                  onClick={() => startEdit(link)}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="text-xs text-destructive hover:underline"
                  onClick={() => onDelete(link.jobInternalFileLinkId, link.rowVersionBase64)}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={handleSubmit} className="space-y-2">
        <h4 className="text-xs font-medium text-muted-foreground">
          {editId != null ? "Edit link" : "Add link"}
        </h4>
        <textarea
          className="w-full rounded border border-border px-2 py-1 text-sm resize-none"
          placeholder="Link HTML (e.g. <a href='...'>label</a>)"
          rows={3}
          value={form.linkDisplayText}
          onChange={(e) => setForm((f) => ({ ...f, linkDisplayText: e.target.value }))}
          aria-label="Link display text"
          required
        />
        <input
          type="text"
          className="w-full rounded border border-border px-2 py-1 text-sm"
          placeholder="Comment (optional)"
          value={form.comment}
          onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))}
          aria-label="Comment"
        />
        <div className="flex gap-2">
          <button
            type="submit"
            className="rounded bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:opacity-90"
          >
            {editId != null ? "Update" : "Add"}
          </button>
          {editId != null && (
            <button
              type="button"
              className="rounded bg-secondary px-3 py-1 text-xs font-medium hover:opacity-90"
              onClick={() => {
                setEditId(null);
                setEditRv("");
                setForm({ linkDisplayText: "", comment: "" });
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
